import { useCurrency, walletSettings } from '@bz/common';
import { getCoinIcon, isFiat } from '@bz/wallet/src/utils';

import { useBetSlipStore } from '../../../useBetSlip';
import { BetSlipType } from '../../../types';

import currencyConvertHint from '~/utils/formatCurrency';

type BetInputProps = {
  amount: string | number;
  matchId?: string;
  code?: string;
  odds?: number;
  betType: BetSlipType;
  class?: string;
  hasError?: boolean;
  disabled?: boolean;
};

/**
 * 货币图标与简称展示
 */
const CurrencySuffix = (props: { currency: string }) => (
  <span class="center text-neutral-main1 -mr-1">
    <img class="size-[18px]" src={getCoinIcon(props.currency)} alt={props.currency} />
  </span>
);

/**
 * 非聚焦状态下的金额显示（包含换算）
 */
const ShowInputContent = (props: { amount: number | string; currency: string; hasError?: boolean }) => {
  return (
    <div
      class={cx(
        'w-full flex justify-between items-center bg-field-darken-bg h-10 px-[.625rem] rounded-lg hover:border-main2 transition-colors cursor-text',
        props.hasError ? '!border-red hover:!border-red' : ''
      )}
    >
      <div class="flex flex-col text-neutral-main1 overflow-hidden">
        <div class="text-sm pt-[.15rem] font-semibold leading-none truncate">{props.amount}</div>
        <div class="text-neutral-main2 pb-1 text-[12px] leading-tight mt-0.5 truncate">
          ≈ {currencyConvertHint(props.amount, props.currency, walletSettings.showCurrency, 'display')}
        </div>
      </div>
      <CurrencySuffix currency={props.currency} />
    </div>
  );
};

export const BetInput = (props: BetInputProps) => {
  const [store, dispatch] = useBetSlipStore();
  const { latestCurrency } = useCurrency();
  const [isFocus, setIsFocus] = createSignal(false);

  let inputRef!: HTMLInputElement;

  const isFiatMode = createMemo(() => isFiat(latestCurrency()));

  const displayValue = createMemo(() => {
    if (store.betType !== BetSlipType.Single) {
      return !!store.comboAmount ? store.comboAmount : '';
    } else {
      const rawValue = props.betType === BetSlipType.Single ? store.odds.find((o) => o.code === props.code)?.amount : 0;
      // 如果值是 0、undefined 或 null，返回空字符串，激活 input 的 placeholder
      return rawValue === 0 || !rawValue ? '' : rawValue;
    }
  });

  const shouldShowDisplay = createMemo(() => {
    return !isFiatMode() && !!displayValue() && !isFocus();
  });

  // 更新逻辑
  const handleInput = (value: string | number) => {
    const amount = value === '' ? 0 : Number(value);
    const actions = {
      [BetSlipType.Single]: () => dispatch({ type: 'UPDATE_ODD_AMOUNT', code: props.code!, amount }),
      [BetSlipType.Combo]: () => dispatch({ type: 'SET_COMBO_AMOUNT', amount }),
      [BetSlipType.System]: () => dispatch({ type: 'SET_COMBO_AMOUNT', amount }),
    };

    actions[props.betType]?.();
  };

  return (
    <div class={cx('relative w-full', props.class)}>
      <Show
        when={shouldShowDisplay()}
        fallback={
          <Input.Number
            ref={inputRef}
            disabled={!!props.disabled}
            autofocus={isFocus()}
            value={displayValue() as any}
            onFocus={() => setIsFocus(true)}
            onBlur={() => setIsFocus(false)}
            onInput={handleInput}
            preserveTrailingZeros={Number(currencyConfig[latestCurrency()]?.currencyType) === 0}
            suffix={
              <div class="mr-2">
                <CurrencySuffix currency={latestCurrency()} />
              </div>
            }
            class={cx(
              'w-full h-10 border-main3 bg-field-darken-bg caption-m-semibold transition-all hover:border-main2 focus-within:border-primary-main [&_input::placeholder]:text-neutral-main5 [&_input:focus::placeholder]:text-neutral-main7',
              props.hasError ? '!border-red hover:!border-red focus-within:!border-red' : ''
            )}
            placeholder={t('Stake amount')}
          />
        }
      >
        <div
          onClick={() => {
            setIsFocus(true);
            inputRef?.focus();
          }}
        >
          <ShowInputContent amount={displayValue()} currency={latestCurrency()} hasError={props.hasError} />
        </div>
      </Show>
    </div>
  );
};
